import { Fragment, useMemo } from 'react';
import { HandleOpenNewLegModal } from '@lp/features/RoutePlanner/types';
import { SegmentTypeEnum, AviaLegTypes, GroundLegTypes, RouteTypeEnum } from '@/api/apis/route';
import { RouteSegmentType } from '@/types/route';
import { EmptyRouteSegment } from './EmptyRouteSegment';
import { SegmentLeg } from './SegmentLeg';

type SegmentLegsListProps = {
  type?: 'logistics' | 'suggestion';
  routeType: RouteTypeEnum;
  legs: (AviaLegTypes | GroundLegTypes)[];
  segment: RouteSegmentType;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  disableHintText?: string;
  handleAddNewLeg?: (data: Partial<HandleOpenNewLegModal>) => void;
  handleHubAssign?: (segmentType: SegmentTypeEnum) => void;
  handleRemoveHub?: (segmentType: SegmentTypeEnum) => void;
  handleLegInfoClick?: (leg: AviaLegTypes | GroundLegTypes, segmentId: string | null, prevLegId: string | null) => void;
};

export const SegmentLegsList = ({
  type = 'logistics',
  routeType,
  legs,
  segment,
  isReadOnly,
  isDisabled,
  disableHintText,
  handleAddNewLeg,
  handleHubAssign,
  handleRemoveHub,
  handleLegInfoClick,
}: SegmentLegsListProps) => {
  const hasStartLeg = useMemo(() => {
    const firstLeg = legs[0]?.departureLocation.locationId;
    const startPoint = segment.startLocation.locationId;

    return firstLeg === startPoint;
  }, [legs, segment]);

  const hasEndLeg = useMemo(() => {
    const lastLeg = legs[legs.length - 1]?.arrivalLocation.locationId;
    const endPoint = segment.endLocation.locationId;

    return lastLeg === endPoint;
  }, [legs, segment]);

  if (!legs || !legs.length) {
    return (
      <EmptyRouteSegment
        segment={segment}
        isReadOnly={!!isReadOnly}
        isDisabled={!!isDisabled}
        disableHintText={disableHintText}
        showStartPoint
        showEndPoint
        showTime
        showAddLeg
        handleAddNewLeg={handleAddNewLeg}
        handleHubAssign={handleHubAssign}
        handleRemoveHub={handleRemoveHub}
      />
    );
  }

  return (
    <div className="flex items-stretch grow gap-4 pr-4">
      <div className="flex flex-col w-full">
        {!hasStartLeg && (
          <EmptyRouteSegment
            segment={segment}
            isReadOnly={!!isReadOnly}
            isDisabled={!!isDisabled}
            disableHintText={disableHintText}
            showStartPoint
            handleAddNewLeg={handleAddNewLeg}
            handleHubAssign={handleHubAssign}
            handleRemoveHub={handleRemoveHub}
          />
        )}
        {legs.map((leg, index, array) => {
          const isStart = !index;
          const isPrevLegDisconnected =
            array[index - 1]?.arrivalLocation.locationId !== leg.departureLocation.locationId;

          return (
            <Fragment key={leg.id}>
              {!isStart && isPrevLegDisconnected && (
                <EmptyRouteSegment
                  segment={segment}
                  isReadOnly={!!isReadOnly}
                  isDisabled={!!isDisabled}
                  disableHintText={disableHintText}
                  prevLegId={array[index - 1].id}
                  handleAddNewLeg={handleAddNewLeg}
                  handleHubAssign={handleHubAssign}
                  handleRemoveHub={handleRemoveHub}
                />
              )}
              <SegmentLeg
                key={leg.id}
                type={type}
                routeType={routeType}
                leg={leg}
                prevLeg={array[index - 1]}
                segment={segment}
                showAddLeg={hasEndLeg && index === array.length - 1}
                isReadOnly={!!isReadOnly}
                isDisabled={!!isDisabled}
                disableHintText={disableHintText}
                handleAddNewLeg={handleAddNewLeg}
                handleLegInfoClick={handleLegInfoClick}
              />
            </Fragment>
          );
        })}
        {!hasEndLeg && (
          <EmptyRouteSegment
            segment={segment}
            isReadOnly={!!isReadOnly}
            isDisabled={!!isDisabled}
            disableHintText={disableHintText}
            showEndPoint
            showTime
            showAddLeg
            prevLegId={legs[legs.length - 1]?.id}
            handleAddNewLeg={handleAddNewLeg}
            handleHubAssign={handleHubAssign}
            handleRemoveHub={handleRemoveHub}
          />
        )}
      </div>
    </div>
  );
};
